import * as Highcharts from 'highcharts';
import { YAxisOptions } from 'highcharts/highcharts';
import { getTargetsMinMax } from './targetsUtils';
import { BaseChartProps, GaugeChartProps, YAxisConfig, YAxisConfigArrayItem } from '../types/chartProps';
import { formatPercent } from './formatters';
import { DEFAULT_LOCALE } from './chartDefaults';
import { getPlotLines } from './plotLineUtils';
import { PRIMARY_Y_AXIS_INDEX } from './constants';
import { isBaseChartProps } from './type';
import { escapeHtml, escapeOptionalHtml } from '@opengov/reporting-types/src/escapeHtml';

const MAX_CATEGORIES_FOR_ALL_LABELS = 5;

export type YAxisConfigApplyFn = (
  options: Highcharts.YAxisOptions,
  params: YAxisConfigArrayItem,
  metaData: {
    chartType: Highcharts.ChartOptions['type'];
    props: BaseChartProps;
  }
) => Highcharts.YAxisOptions;

type FormatterFunction = () => string;

// Highchart formatter uses SI prefixes, we want to replace it after values being formatted
export const formatterOverrideSiPrefix = (value: string) => {
  const SI_PREFIX_MAP = {
    k: 'K',
    M: 'M',
    G: 'B',
    T: 'T',
    P: 'Q',
    E: 'E'
  };

  const prefixKeys = Object.keys(SI_PREFIX_MAP);

  for (const prefix of prefixKeys) {
    if (value.includes(prefix)) {
      return value.replace(prefix, SI_PREFIX_MAP[prefix as keyof typeof SI_PREFIX_MAP]);
    }
  }

  return value;
};

const axisLabelFormatters = {
  percent: function (this: Highcharts.AxisLabelsFormatterContextObject) {
    return formatPercent(this.value as number, DEFAULT_LOCALE);
  },
  percentSuffix: function (this: Highcharts.AxisLabelsFormatterContextObject) {
    return `${this.axis.defaultLabelFormatter.call(this)}%`;
  },
  currency: function (this: Highcharts.AxisLabelsFormatterContextObject) {
    const nonSIValue = formatterOverrideSiPrefix(this.axis.defaultLabelFormatter.call(this).replace(/ /g, ','));
    return `$${nonSIValue}`;
  },
  default: function (this: Highcharts.AxisLabelsFormatterContextObject) {
    const nonSIValue = formatterOverrideSiPrefix(this.axis.defaultLabelFormatter.call(this).replace(/ /g, ','));
    return nonSIValue;
  }
};

export function isPrimaryYAxisIndex(index: number) {
  return index === PRIMARY_Y_AXIS_INDEX;
}

export function supportsMultipleYAxis(yAxis: YAxisConfig | YAxisConfigArrayItem[]): yAxis is YAxisConfigArrayItem[] {
  return Array.isArray(yAxis);
}

function supportsSingleYAxis(yAxis: YAxisConfig | YAxisConfigArrayItem[]): yAxis is YAxisConfig {
  return !Array.isArray(yAxis);
}

export function getYAxisIdConfigMap(yAxis: YAxisConfigArrayItem[]) {
  return yAxis.reduce((acc, yAxisConfig) => {
    acc[yAxisConfig.id] = yAxisConfig;
    return acc;
  }, {} as Record<string, YAxisConfigArrayItem>);
}

export function getYAxisIdIndexMap(yAxis: YAxisConfigArrayItem[]) {
  return yAxis.reduce((acc, yAxisConfig, index) => {
    acc[yAxisConfig.id] = index;
    return acc;
  }, {} as Record<string, number>);
}

function escapeCategoryLabel(this: Highcharts.AxisLabelsFormatterContextObject) {
  return escapeHtml(this.axis.defaultLabelFormatter.call(this));
}

export function applyXAxisOptions(options: Highcharts.Options, params: BaseChartProps) {
  const categoryCount = params.xAxis.categories?.length ?? 0;
  options.xAxis = {
    ...options.xAxis,
    title: {
      text: escapeHtml(params.xAxis.title ?? 'Category')
    },
    categories: params.xAxis.categories,
    labels: {
      ...(options.xAxis as Highcharts.XAxisOptions)?.labels,
      formatter: escapeCategoryLabel,
      // Show all labels when ≤ MAX_CATEGORIES_FOR_ALL_LABELS categories
      ...(categoryCount <= MAX_CATEGORIES_FOR_ALL_LABELS && { step: 1 })
    }
  };
  return options;
}

export function applyYAxisTitleOptions(options: Highcharts.Options, params: BaseChartProps) {
  options.yAxis = {
    ...options.yAxis,
    title: {
      text: supportsSingleYAxis(params.yAxis) ? escapeOptionalHtml(params.yAxis.title) : 'Amount'
    }
  };

  return options;
}

export const applyYAxisTitleOptionsForConfig: YAxisConfigApplyFn = (
  options: Highcharts.YAxisOptions,
  params: YAxisConfigArrayItem
) => {
  options.title = {
    text: escapeOptionalHtml(params.title)
  };
  return options;
};

export const getYAxisLabelFormatterFromAxisConfig = (config: YAxisConfigArrayItem) => {
  if (config.stacking === 'percent') {
    return axisLabelFormatters.percentSuffix;
  }

  if (config.isPercent) {
    return axisLabelFormatters.percent;
  }

  if (config.valueDisplayType === 'currency') {
    return axisLabelFormatters.currency;
  }

  return axisLabelFormatters.default;
};

export const getYAxisLabelFormatter = (params: BaseChartProps | GaugeChartProps): FormatterFunction => {
  if (isBaseChartProps(params) && params.stacking === 'percent') {
    return axisLabelFormatters.percentSuffix;
  }

  if (params.isPercentChart) {
    return axisLabelFormatters.percent;
  }

  if (params.valueDisplayType === 'currency') {
    return axisLabelFormatters.currency;
  }

  return axisLabelFormatters.default;
};

export function applyYAxisLabelsOptions(options: Highcharts.Options, params: BaseChartProps | GaugeChartProps) {
  options.yAxis = {
    ...options.yAxis,
    labels: {
      ...(options.yAxis as YAxisOptions).labels,
      formatter: getYAxisLabelFormatter(params)
    }
  };

  return options;
}

export const applyYAxisLabelsOptionsForConfig: YAxisConfigApplyFn = (
  options: Highcharts.YAxisOptions,
  params: YAxisConfigArrayItem
) => {
  options.labels = {
    ...(options.labels ?? {}),
    formatter: getYAxisLabelFormatterFromAxisConfig(params)
  };
  return options;
};

export function applyYAxisPlotLinesOptions(options: Highcharts.Options, params: BaseChartProps) {
  options.yAxis = {
    ...options.yAxis,
    plotLines: getPlotLines(params.targets ?? [], options.chart?.type)
  };
  return options;
}

export const applyYAxisPlotLinesOptionsForConfig: YAxisConfigApplyFn = (options, params, metaData) => {
  options.plotLines = getPlotLines(params.targets ?? [], metaData.chartType);
  return options;
};

/** Matches Highcharts default maxPadding so peak markers are not clipped at the plot edge. */
const Y_AXIS_EDGE_PADDING_RATIO = 0.05;

export function getPaddedYAxisMinMax(min: number, max: number) {
  const range = max - min;
  const padding = range === 0 ? Math.abs(max) * Y_AXIS_EDGE_PADDING_RATIO || 1 : range * Y_AXIS_EDGE_PADDING_RATIO;
  const paddedMin = min - padding;
  const paddedMax = max + padding;
  return {
    // Keep $0 baseline for all-positive / all-negative currency charts
    min: min >= 0 && paddedMin < 0 ? 0 : paddedMin,
    max: max <= 0 && paddedMax > 0 ? 0 : paddedMax
  };
}

export function applyYAxisMinMaxOptions(options: Highcharts.Options, params: BaseChartProps) {
  if (params.stacking === 'percent') {
    return options;
  }
  const { min: minTargetValue, max: maxTargetValue } = getTargetsMinMax(params.targets ?? []);
  const { min: minSeriesValue, max: maxSeriesValue } = getMinMaxYAxisSeriesValue(
    params,
    options.plotOptions?.column?.stacking
  );
  const { min, max } = getPaddedYAxisMinMax(
    Math.min(minTargetValue, minSeriesValue),
    Math.max(maxTargetValue, maxSeriesValue)
  );

  options.yAxis = {
    ...options.yAxis,
    endOnTick: false,
    min,
    max
  };

  return options;
}

export const applyYAxisMinMaxOptionsForConfig: YAxisConfigApplyFn = (options, params, metaData) => {
  if (params.stacking === 'percent') {
    return options;
  }

  const { min: minTargetValue, max: maxTargetValue } = getTargetsMinMax(params.targets ?? []);
  const { min: minSeriesValue, max: maxSeriesValue } = getMinMaxYAxisSeriesValue(
    metaData.props,
    params.stacking,
    params.id
  );
  const { min, max } = getPaddedYAxisMinMax(
    Math.min(minTargetValue, minSeriesValue),
    Math.max(maxTargetValue, maxSeriesValue)
  );

  options = {
    ...options,
    endOnTick: false,
    min,
    max
  };
  return options;
};

export function applyYAxisOptions(options: Highcharts.Options, params: BaseChartProps) {
  if (supportsMultipleYAxis(params.yAxis)) {
    options.yAxis = params.yAxis.map((yAxisConfig, index) => {
      const existingYAxis = Array.isArray(options.yAxis) ? options.yAxis[index] : options.yAxis;
      const baseYAxisOptions: Highcharts.YAxisOptions = {
        ...existingYAxis,
        id: yAxisConfig.id, // apply id to the yAxis options
        opposite: !isPrimaryYAxisIndex(index), // apply opposite position for secondary yAxis
        gridLineWidth: isPrimaryYAxisIndex(index) ? 1 : 0 // apply grid line width for primary yAxis only
      };
      return [
        applyYAxisTitleOptionsForConfig,
        applyYAxisLabelsOptionsForConfig,
        applyYAxisMinMaxOptionsForConfig,
        applyYAxisPlotLinesOptionsForConfig
      ].reduce((acc, fn) => fn(acc, yAxisConfig, { chartType: options.chart?.type, props: params }), baseYAxisOptions);
    });
    return options;
  }

  return [applyYAxisTitleOptions, applyYAxisLabelsOptions, applyYAxisMinMaxOptions, applyYAxisPlotLinesOptions].reduce(
    (acc, fn) => fn(acc, params),
    options
  );
}

export function getMinMaxYAxisSeriesValue(
  props: BaseChartProps,
  stacking: Highcharts.OptionsStackingValue | undefined,
  yAxisId?: string
) {
  const { series, xAxis } = props;

  // Extract all values from the series
  const values = yAxisId
    ? series.filter((el) => el.yAxisId === yAxisId).map((el) => el.data.map((d) => d.value ?? 0))
    : series.map((el) => el.data.map((d) => d.value ?? 0));

  if (stacking === 'normal') {
    return xAxis.categories.reduce(
      (acc, _, i) => {
        const sumObj = values.reduce(
          (sumAcc, cur) => {
            if (cur[i] > 0) {
              sumAcc.positive += cur[i];
            } else {
              sumAcc.negative += cur[i];
            }
            return sumAcc;
          },
          { positive: 0, negative: 0 }
        );

        acc.max = Math.max(acc.max, sumObj.positive);
        acc.min = Math.min(acc.min, sumObj.negative);
        return acc;
      },
      { min: 0, max: 0 }
    );
  }

  return values.flat().reduce(
    (acc, cur) => {
      if (cur > acc.max) {
        acc.max = cur;
      } else if (cur < acc.min) {
        acc.min = cur;
      }
      return acc;
    },
    { max: Number.NEGATIVE_INFINITY, min: Number.POSITIVE_INFINITY }
  );
}
